import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { appSyncService } from "./service";
import type {
  ChatMessage,
  ChatMessageConnection,
  DataPullStatus,
  DataPullStatusConnection,
  GetMyMessagesByContextInput,
  GetMyRunsInput,
  GetPullStatusesInput,
  OnNewMessageInput,
  OnNewRunInput,
  OnPullStatusUpdateInput,
  OnViewTransformUpdateInput,
  Run,
  RunConnection,
  ViewTransform,
} from "./types";
import { subscribeToViewTransformUpdates } from "./view_transforms";

// Hook for getting messages by context
export const useMyMessagesByContext = (
  input: GetMyMessagesByContextInput,
  options: { enabled?: boolean } = {}
) => {
  const [data, setData] = useState<ChatMessageConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchingRef = useRef(false);
  const inputRef = useRef(input);

  // Update ref when input changes (based on actual values, not object reference)
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId, input.contextId, input.limit, input.nextToken]);

  // Memoize input values to avoid infinite loops from object reference changes
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId, input.contextId, input.limit, input.nextToken]
  );

  // inputKey is intentionally included to trigger callback recreation when input values change
  const fetchMessages = useCallback(async () => {
    if (!options.enabled || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await appSyncService.getMyMessagesByContext(inputRef.current);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, options.enabled]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    data,
    loading,
    error,
    refetch: fetchMessages,
  };
};

// Hook for getting my runs
export const useMyRuns = (input: GetMyRunsInput, options: { enabled?: boolean } = {}) => {
  const [data, setData] = useState<RunConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchingRef = useRef(false);
  const inputRef = useRef(input);

  // Update ref when input changes (based on actual values, not object reference)
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId, input.limit, input.nextToken]);

  // Memoize input values to avoid infinite loops from object reference changes
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId, input.limit, input.nextToken]
  );

  // inputKey is intentionally included to trigger callback recreation when input values change
  const fetchRuns = useCallback(async () => {
    if (!options.enabled || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await appSyncService.getMyRuns(inputRef.current);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, options.enabled]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  return {
    data,
    loading,
    error,
    refetch: fetchRuns,
  };
};

// Hook for subscribing to new messages
export const useNewMessageSubscription = (
  input: OnNewMessageInput,
  options: { enabled?: boolean } = {}
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Memoize input to avoid re-subscribing when object reference changes
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId, input.contextId]
  );

  // Use ref to always have current input without causing re-subscriptions
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputRef = useRef(input);
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId, input.contextId]);

  // inputKey is intentionally included to trigger subscription recreation when input values change
  useEffect(() => {
    if (!options.enabled) {
      // Clean up subscription when disabled
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      return;
    }

    setError(null);

    // Unsubscribe from previous subscription if it exists
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Subscribe asynchronously (subscription method is now async to fetch ID token)
    appSyncService
      .subscribeToNewMessages(
        inputRef.current,
        (message) => {
          setMessages((prev) => [message, ...prev]);
        },
        (err) => {
          setError(err);
        }
      )
      .then((subscription) => {
        subscriptionRef.current = subscription;
      })
      .catch((err) => {
        setError(err);
      });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [inputKey, options.enabled]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    error,
    clearMessages,
  };
};

// Hook for subscribing to new runs
export const useNewRunSubscription = (
  input: OnNewRunInput,
  options: { enabled?: boolean } = {}
) => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Memoize input to avoid re-subscribing when object reference changes
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId]
  );

  // Use ref to always have current input without causing re-subscriptions
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  const inputRef = useRef(input);
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId]);

  // inputKey is intentionally included to trigger subscription recreation when input values change
  useEffect(() => {
    if (!options.enabled) {
      // Clean up subscription when disabled
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      return;
    }

    setError(null);

    // Unsubscribe from previous subscription if it exists
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Subscribe asynchronously (subscription method is now async to fetch ID token)
    appSyncService
      .subscribeToNewRuns(
        inputRef.current,
        (run) => {
          setRuns((prev) => [run, ...prev]);
        },
        (err) => {
          setError(err);
        }
      )
      .then((subscription) => {
        subscriptionRef.current = subscription;
      })
      .catch((err) => {
        setError(err);
      });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [inputKey, options.enabled]);

  const clearRuns = useCallback(() => {
    setRuns([]);
  }, []);

  return {
    runs,
    error,
    clearRuns,
  };
};

// Hook for subscribing to view transform updates
// Always uses the filtered subscription (onViewTransformUpdateFiltered) which requires all parameters
// This ensures each instance only receives updates for its specific transform, providing proper isolation
export const useViewTransformSubscription = (
  input: OnViewTransformUpdateInput,
  options: { enabled?: boolean } = {}
) => {
  const [viewTransforms, setViewTransforms] = useState<ViewTransform[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isReady, setIsReady] = useState(false); // Track if subscription is ready
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const inputRef = useRef(input);
  const transformIdRef = useRef(input.transformId);
  // Memoize input to avoid re-subscribing when object reference changes
  // Include all filter parameters in the key so subscription is recreated when filters change
  const inputKey = useMemo(
    () =>
      JSON.stringify({
        organizationId: input.organizationId,
        transformId: input.transformId,
        transformationSettings: input.transformationSettings,
        runSettings: input.runSettings,
      }),
    [input.organizationId, input.transformId, input.transformationSettings, input.runSettings]
  );

  // Update ref when input changes (based on actual values, not object reference)
  // Intentionally using specific properties instead of 'input' object to avoid infinite loops
  // CRITICAL: Update ref when transformationSettings or runSettings change so subscription uses correct values
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId, input.transformId, input.transformationSettings, input.runSettings]);

  // Update transformId ref when it changes (without triggering re-subscription)
  useEffect(() => {
    transformIdRef.current = input.transformId;
  }, [input.transformId]);

  // inputKey is intentionally included to trigger subscription recreation when input values change
  useEffect(() => {
    let isMounted = true;
    let currentSubscription: { unsubscribe: () => void } | null = null;

    if (!options.enabled) {
      // Clean up subscription when disabled
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      setIsReady(false);
      return;
    }

    setError(null);

    // Unsubscribe from previous subscription if it exists BEFORE creating new one
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Clear stale VTs from previous params to prevent matchedViewTransform
    // (which matches by prefix, not exact params) from picking a VT with the
    // wrong hash during the brief window before the new subscription delivers.
    // This eliminates the race condition where an old VT's ID overwrites the
    // current viewTransformId mid-parse, causing a silent bail + stuck loading.
    setViewTransforms([]);

    // Subscribe asynchronously (subscription method is now async to fetch ID token)
    // const subscriptionStartTimeRef = { current: performance.now() };

    // Build subscription input with all required parameters for filtered subscription
    // All parameters are required for strict server-side filtering
    // CRITICAL: Use input directly (not inputRef.current) to ensure we get the latest transformationSettings
    // The inputKey dependency ensures this effect runs when transformationSettings changes
    const subscriptionInput: OnViewTransformUpdateInput = {
      organizationId: input.organizationId,
      transformId: input.transformId,
      transformationSettings: input.transformationSettings,
      runSettings: input.runSettings,
    };

    subscribeToViewTransformUpdates(
      subscriptionInput,
      (viewTransform) => {
        // Only update state if component is still mounted
        // Note: With server-side filtering, we should only receive matching transforms
        // But keep client-side check as a safety net
        if (isMounted) {
          // Clear any previous subscription error on successful update (e.g. after reconnect)
          setError(null);
          // Filter by transformId if specified - skip updates for other transforms (safety net)
          // With filtered subscription, this should never happen, but keep as safety check
          if (transformIdRef.current && viewTransform.transformId !== transformIdRef.current) {
            // Received transform with wrong transformId (should not happen with filtered subscription)
            return;
          }

          setViewTransforms((prev) => {
            // Update existing or add new
            const existingIndex = prev.findIndex((vt) => vt.id === viewTransform.id);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = viewTransform;
              return updated;
            }
            const newArray = [viewTransform, ...prev];
            return newArray;
          });
        }
      },
      (err) => {
        // Only set error if component is still mounted
        if (isMounted) {
          // Subscription error
          setError(err);
        }
      }
    )
      .then((subscription) => {
        // Only store subscription if component is still mounted
        if (isMounted) {
          currentSubscription = subscription;
          subscriptionRef.current = subscription;
          setIsReady(true); // Mark subscription as ready
        } else {
          // Component unmounted before subscription was established, clean it up
          subscription.unsubscribe();
        }
      })
      .catch((err) => {
        // Only set error if component is still mounted
        if (isMounted) {
          // Failed to establish subscription
          setError(err);
        }
      });

    return () => {
      isMounted = false;
      // Clean up both the ref and any pending subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (currentSubscription) {
        currentSubscription.unsubscribe();
        currentSubscription = null;
      }
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inputKey derives from all input fields; listing them individually is redundant
  }, [inputKey, options.enabled]);

  // Auto-cleanup: Remove transforms older than 3 minutes
  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const threeMinutesAgo = now - 3 * 60 * 1000; // 3 minutes in milliseconds

      setViewTransforms((prev) => {
        const filtered = prev.filter((vt) => {
          const updatedAt = new Date(vt.updatedAt).getTime();
          // Keep transforms that were updated within the last 3 minutes
          return updatedAt > threeMinutesAgo;
        });

        // Only update if we actually removed something (avoid unnecessary re-renders)
        if (filtered.length !== prev.length) {
          return filtered;
        }

        return prev;
      });
    }, 60000); // Check every minute

    return () => {
      clearInterval(cleanupInterval);
    };
  }, [options.enabled]);

  const clearViewTransforms = useCallback(() => {
    setViewTransforms([]);
  }, []);

  return {
    viewTransforms,
    error,
    clearViewTransforms,
    isReady, // Export subscription readiness state
  };
};

// Hook for getting pull statuses
export const usePullStatuses = (
  input: GetPullStatusesInput,
  options: { enabled?: boolean } = {}
) => {
  const [data, setData] = useState<DataPullStatusConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchingRef = useRef(false);
  const inputRef = useRef(input);

  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId, input.limit, input.nextToken]);

  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId, input.limit, input.nextToken]
  );

  const fetchStatuses = useCallback(async () => {
    if (!options.enabled || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await appSyncService.getPullStatuses(inputRef.current);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, options.enabled]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return {
    data,
    loading,
    error,
    refetch: fetchStatuses,
  };
};

// Hook for subscribing to pull status updates
export const usePullStatusSubscription = (
  input: OnPullStatusUpdateInput,
  options: { enabled?: boolean } = {}
) => {
  const [statuses, setStatuses] = useState<Map<string, DataPullStatus>>(new Map());
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const inputKey = useMemo(
    () => JSON.stringify(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.organizationId]
  );

  const inputRef = useRef(input);
  useEffect(() => {
    inputRef.current = input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.organizationId]);

  useEffect(() => {
    if (!options.enabled) {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      return;
    }

    setError(null);

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    appSyncService
      .subscribeToPullStatusUpdates(
        inputRef.current,
        (status) => {
          setStatuses((prev) => {
            const next = new Map(prev);
            next.set(status.sourceId, status);
            return next;
          });
        },
        (err) => {
          setError(err);
        }
      )
      .then((subscription) => {
        subscriptionRef.current = subscription;
      })
      .catch((err) => {
        setError(err);
      });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [inputKey, options.enabled]);

  return {
    statuses,
    error,
  };
};
